import axios from "axios";
import Cookies from "js-cookie";
import localStorageAdapter from "./localStorageAdapter";

import {
  getCookies,
  setCookies,
  accessTokenCookieKey,
  scopesCookieKey,
  refreshTokenCookieKey,
} from "./cookies";
import {
  BadDataError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  UnprocessibleEntityError,
  InternalServerError,
  ConflictError,
} from "../services/errors.service";

axios.interceptors.response.use((res) => {
  let error;

  switch (res.status) {
    case 400:
      error = new BadDataError();
      break;
    case 401:
      error = new UnauthorizedError();
      break;
    case 403:
      error = new ForbiddenError();
      break;
    case 404:
      error = new NotFoundError();
      break;
    case 409:
      error = new ConflictError();
      break;
    case 422:
      error = new UnprocessibleEntityError();
      break;
    case 500:
      error = new InternalServerError();
      break;
    default:
      break;
  }

  if (error) {
    error.response = res;
    throw error;
  }

  return res;
});

const authAxios = axios.create({
  baseURL: process.env.NODE_ENV === "development" ? "/auth-proxy" : window.REACT_APP_AUTH_URL,
});

const vehicleAxios = axios.create({
  baseURL: window.REACT_APP_VEHICLE_API_URL,
  adapter: localStorageAdapter,
});

const reservationAxios = axios.create({
  baseURL: window.REACT_APP_RESERVATION_API_URL,
});

const riderAxios = axios.create({
  baseURL: window.REACT_APP_CONSUMER_AUTH_URL,
});

const billingAxios = axios.create({
  baseURL: window.REACT_APP_PROXY_BILLING_URL,
});

const registrationAxios = axios.create({
  baseURL: "/proxy",
});

const geocodingAxios = axios.create({
  baseURL: window.REACT_APP_GEOCODING_URL,
  params: { key: window.REACT_APP_GEOCODING_KEY },
});

const geofenceAxios = axios.create({
  baseURL: window.REACT_APP_GEOFENCE_API_URL,
});

const setAuthToken = (config) => {
  const token = getCookies(accessTokenCookieKey);
  if (token) {
    config.headers.Authorization = `Bearer superpedestrian-auth-server ${token}`;
  }
  return config;
};

const setRegistrationAuthToken = (config) => {
  const token = getCookies(accessTokenCookieKey);
  if (token) {
    switch (window.REACT_APP_CLUSTER_NAME) {
      case "EU":
        config.headers.Authorization = `Bearer superpedestrian-auth-server-eu ${token}`;
        break;
      case "Staging":
        config.headers.Authorization = `Bearer superpedestrian-auth-server-stag ${token}`;
        break;
      case "Link":
      default:
        config.headers.Authorization = `Bearer superpedestrian-auth-server ${token}`;
        // check that the string includes 'auth' as protection against any "true", "false", or "None" nonsense
        if (
          window.REACT_APP_REGISTRATION_CLUSTER_AUTH_HEADER &&
          window.REACT_APP_REGISTRATION_CLUSTER_AUTH_HEADER.includes("auth")
        ) {
          config.headers.Authorization = `Bearer ${window.REACT_APP_REGISTRATION_CLUSTER_AUTH_HEADER} ${token}`;
        }
        break;
    }
  }
  return config;
};

const refreshAuthToken = async (error) => {
  const originalRequest = error.config;
  const token = getCookies(refreshTokenCookieKey);
  if (error?.response?.status === 401 && token) {
    try {
      const res = await authAxios.post("/o/token/", {
        refresh_token: token,
        grant_type: "refresh_token",
        client_id: window.REACT_APP_AUTH_CLIENT_ID,
      });
      const {
        access_token: accessToken,
        scope,
        refresh_token: refreshToken,
        expires_in: expiresIn,
      } = res.data;
      setCookies(scopesCookieKey, scope, expiresIn);
      setCookies(accessTokenCookieKey, accessToken, expiresIn);
      setCookies(refreshTokenCookieKey, refreshToken);
      originalRequest.headers.Authorization = `Bearer superpedestrian-auth-server ${accessToken}`;
      return axios(originalRequest);
    } catch (err) {
      Cookies.remove("sp_refresh_token");
    }
  }
  return Promise.reject(error);
};

const vehicleSetTokenInterceptor = vehicleAxios.interceptors.request.use(setAuthToken);
const reservationSetTokenInterceptor = reservationAxios.interceptors.request.use(setAuthToken);
const riderSetTokenInterceptor = riderAxios.interceptors.request.use(setAuthToken);
const billingSetTokenInterceptor = billingAxios.interceptors.request.use(setAuthToken);
const registrationSetTokenInterceptor =
  registrationAxios.interceptors.request.use(setRegistrationAuthToken);
const geofenceSetTokenInterceptor = geofenceAxios.interceptors.request.use(setAuthToken);

const vehicleRefreshTokenInterceptor = vehicleAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);
const reservationRefreshTokenInterceptor = reservationAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);
const riderRefreshTokenInterceptor = riderAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);
const billingRefreshTokenInterceptor = billingAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);
const registrationRefreshTokenInterceptor = registrationAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);
const geofenceRefreshTokenInterceptor = geofenceAxios.interceptors.response.use(
  (response) => response,
  refreshAuthToken,
);

export {
  authAxios,
  vehicleAxios,
  reservationAxios,
  riderAxios,
  billingAxios,
  registrationAxios,
  geocodingAxios,
  geofenceAxios,
  vehicleSetTokenInterceptor,
  reservationSetTokenInterceptor,
  riderSetTokenInterceptor,
  billingSetTokenInterceptor,
  registrationSetTokenInterceptor,
  geofenceSetTokenInterceptor,
  vehicleRefreshTokenInterceptor,
  reservationRefreshTokenInterceptor,
  riderRefreshTokenInterceptor,
  billingRefreshTokenInterceptor,
  registrationRefreshTokenInterceptor,
  geofenceRefreshTokenInterceptor,
};
